import useAuth from '../auth/auth';
import { useABTest } from '../contexts/ABTestContext';
import {
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_50,
  GRAY_500,
  GRAY_600,
  GRAY_900,
} from 'theme/grayScale';
import { LGM_ORANGE } from 'theme/colors';

const DevTools: React.FC = () => {
  const { userId, roles } = useAuth();
  const {
    tests,
    getTestValue,
    setTestValue,
    resetTests,
    enumTests,
    getEnumValue,
    setEnumValue,
  } = useABTest();
  const buildId = process.env.REACT_APP_ENV ?? 'Not Set';

  return (
    <div style={styles.containerStyle}>
      <div style={styles.contentWrapper}>
        <div style={styles.leftSection}>
          <p style={styles.infoText}>
            <strong>User_ID:</strong> {userId}
          </p>
          <p style={styles.infoText}>
            <strong>Build:</strong> {buildId}
          </p>
          <p style={styles.infoText}>
            <strong>Roles:</strong>{' '}
            {roles.length > 0 ? roles.join(', ') : 'No roles assigned'}
          </p>
        </div>

        <div style={styles.rightSection}>
          <div style={styles.abTestHeader}>
            <h3 style={styles.abTestTitle}>A/B Tests</h3>
            <button onClick={resetTests} style={styles.resetButton}>
              Reset
            </button>
          </div>
          {tests.length === 0 ? (
            <p style={styles.noTests}>No A/B tests configured</p>
          ) : (
            <div style={styles.testGrid}>
              {tests.map(test => {
                const isChecked = getTestValue(test.key);
                return (
                  <div
                    key={test.key}
                    style={styles.testItem}
                    onClick={() => setTestValue(test.key, !isChecked)}>
                    <div style={styles.testInfo}>
                      <span style={styles.testLabel}>{test.name}</span>
                      <p style={styles.testDescription}>{test.description}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={e => {
                        e.stopPropagation();
                        setTestValue(test.key, e.target.checked);
                      }}
                      onClick={e => e.stopPropagation()}
                      style={styles.checkbox}
                    />
                  </div>
                );
              })}
            </div>
          )}
          {enumTests.length > 0 && (
            <div style={styles.testGrid}>
              {enumTests.map(test => (
                <div key={test.key} style={styles.enumTestItem}>
                  <div style={styles.testInfo}>
                    <span style={styles.testLabel}>{test.name}</span>
                    <p style={styles.testDescription}>{test.description}</p>
                  </div>
                  <select
                    value={getEnumValue(test.key)}
                    onChange={e => setEnumValue(test.key, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    style={styles.enumSelect}>
                    {test.options.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  containerStyle: {
    backgroundColor: GRAY_50,
    borderBottom: `1px solid ${GRAY_200}`,
    paddingLeft: '3rem',
    paddingRight: '3rem',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    fontSize: '0.75rem',
    color: GRAY_600,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  } as React.CSSProperties,
  contentWrapper: {
    display: 'flex',
    gap: '3rem',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    minWidth: '250px',
  } as React.CSSProperties,
  infoText: {
    margin: 0,
    lineHeight: '1.5',
  } as React.CSSProperties,
  rightSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  } as React.CSSProperties,
  abTestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.25rem',
  } as React.CSSProperties,
  abTestTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: GRAY_900,
    margin: 0,
  } as React.CSSProperties,
  resetButton: {
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    borderRadius: '0.25rem',
    backgroundColor: GRAY_500,
    color: 'white',
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  noTests: {
    color: GRAY_400,
    fontStyle: 'italic',
    margin: 0,
  } as React.CSSProperties,
  testGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '0.75rem',
  } as React.CSSProperties,
  testItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    backgroundColor: 'white',
    borderRadius: '0.25rem',
    border: `1px solid ${GRAY_200}`,
    gap: '0.75rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  } as React.CSSProperties,
  testInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  testLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: GRAY_900,
    cursor: 'pointer',
    lineHeight: '1.2',
  } as React.CSSProperties,
  testDescription: {
    fontSize: '0.65rem',
    color: GRAY_500,
    margin: 0,
    lineHeight: '1.3',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: LGM_ORANGE,
    flexShrink: 0,
  } as React.CSSProperties,
  enumTestItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    backgroundColor: 'white',
    borderRadius: '0.25rem',
    border: `1px solid ${GRAY_200}`,
    gap: '0.75rem',
  } as React.CSSProperties,
  enumSelect: {
    fontSize: '0.75rem',
    padding: '0.125rem 0.25rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    backgroundColor: 'white',
    color: GRAY_900,
    cursor: 'pointer',
    flexShrink: 0,
  } as React.CSSProperties,
};

export default DevTools;
